import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  Divider,
  CircularProgress,
  makeStyles,
} from "@material-ui/core";
import React, { useState } from 'react'
import { CommentReport, PostReport, ProfileReport } from './FlaggedContent';
import defaultAvatars from "../../../../../assets/defaultAvatars.svg";
import { Link } from "react-router-dom";

export const useStyles = makeStyles(theme => ({
  contentCard: {
    backgroundColor: "#1a1a1a",
    border: "1px solid #333",
    marginBottom: theme.spacing(2),
    color: "#fff",
    "&:hover": {
      borderColor: "#FE5A97",
    },
  },
  contentHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  userInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  contentPreview: {
    maxWidth: "200px",
    maxHeight: "200px",
    borderRadius: "8px",
    objectFit: "cover",
  },
  contentPreviewContainer: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: "#2a2a2a",
    borderRadius: "8px",
    border: "1px solid #444",
  },
  contentPreviewImage: {
    maxWidth: "100%",
    maxHeight: "400px",
    borderRadius: "8px",
    objectFit: "contain",
    marginBottom: theme.spacing(1),
  },
  contentPreviewVideo: {
    maxWidth: "100%",
    maxHeight: "400px",
    borderRadius: "8px",
    marginBottom: theme.spacing(1),
  },
  contentPreviewText: {
    padding: theme.spacing(1.5),
    backgroundColor: "#1a1a1a",
    borderRadius: "4px",
    border: "1px solid #333",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  postStatusBadge: {
    marginLeft: theme.spacing(1),
  },
  expandButton: {
    marginTop: theme.spacing(1),
    color: "#FE5A97",
    textTransform: "none",
  },
  actionButtons: {
    display: "flex",
    gap: theme.spacing(1),
  },
  approveButton: {
    backgroundColor: "#4caf50",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#45a049",
    },
  },
  rejectButton: {
    backgroundColor: "#f44336",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#da190b",
    },
  },
}));

type ReportCardProps = {
  report: CommentReport,
  reportType: "post" | "profile" | "comment" | null,
  loadingAvatars: {
    [key: string]: boolean;
  },
  avatarUrls: { [key: string]: string },
  mediaUrls: { [key: string]: string },
  loadingMedia: {
    [key: string]: boolean
  },
  fetchMediaUrl: (mediaUrl: string, key: string) => void,
  setReportStatusDialog: React.Dispatch<React.SetStateAction<{
    open: boolean;
    report: PostReport | ProfileReport | CommentReport | null;
    status: string;
    reportType?: "post" | "profile" | "comment" | null;
  }>>
}

export default function ReportCommentCard({ report, reportType, loadingAvatars, avatarUrls, mediaUrls, loadingMedia, fetchMediaUrl, setReportStatusDialog }: ReportCardProps) {
  const classes = useStyles();
  const [expandedPostReports, setExpandedPostReports] = useState<{
    [key: string]: boolean;
  }>({});
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Card key={report._id} className={classes.contentCard}>
      <CardContent>
        <Box className={classes.contentHeader}>
          <Box className={classes.userInfo}>
            {loadingAvatars[report.commentOwner?._id || ""] ? (
              <Avatar>
                <CircularProgress size={24} />
              </Avatar>
            ) : (
              <Avatar
                src={
                  report.commentOwner?._id && avatarUrls[report.commentOwner._id]
                    ? avatarUrls[report.commentOwner._id]
                    : report.commentOwner?.profileImg
                      ? report.commentOwner.profileImg
                      : defaultAvatars
                }
                alt={report.commentOwner?.username || "User"}
              >
                {!report.commentOwner?.profileImg &&
                  !avatarUrls[report.commentOwner?._id || ""] &&
                  (report.commentOwner?.firstName?.[0] ||
                    report.commentOwner?.username?.[0] ||
                    "U")}
              </Avatar>
            )}
            <Box>
              <Typography variant="h6" style={{ color: "#fff" }}>
                {report.commentOwner?.username ? (
                  <Link
                    to={`/${report.commentOwner.username}`}
                    style={{
                      color: "#FE5A97",
                      textDecoration: "none",
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.textDecoration = "underline";
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.textDecoration = "none";
                    }}
                  >
                    {report.commentOwner.username}
                  </Link>
                ) : (
                  report.commentOwner?.firstName || "Unknown User"
                )}
              </Typography>
              <Typography variant="body2" style={{ color: "#ccc" }}>
                {report.commentOwner?.email || "No email available"}
              </Typography>
            </Box>
          </Box>
          {report.reporter && (
            <Box
              className={classes.userInfo}
              style={{ marginTop: "12px", marginBottom: "8px" }}
            >
              {loadingAvatars[report.reporter._id] ? (
                <Avatar>
                  <CircularProgress size={24} />
                </Avatar>
              ) : (
                <Avatar
                  src={
                    report.reporter._id && avatarUrls[report.reporter._id]
                      ? avatarUrls[report.reporter._id]
                      : report.reporter.profileImg
                        ? report.reporter.profileImg
                        : defaultAvatars
                  }
                  alt={report.reporter.username || "Reporter"}
                  style={{ width: 32, height: 32 }}
                >
                  {!report.reporter.profileImg &&
                    !avatarUrls[report.reporter._id] &&
                    (report.reporter.firstName?.[0] ||
                      report.reporter.username?.[0] ||
                      "R")}
                </Avatar>
              )}
              <Box>
                <Typography
                  variant="body2"
                  style={{ color: "#999", fontSize: "12px" }}
                >
                  Reported by:
                </Typography>
                <Typography variant="body2" style={{ color: "#fff" }}>
                  {report.reporter.username ? (
                    <Link
                      to={`/${report.reporter.username}`}
                      style={{
                        color: "#FE5A97",
                        textDecoration: "none",
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.textDecoration = "underline";
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.textDecoration = "none";
                      }}
                    >
                      {report.reporter.username}
                    </Link>
                  ) : (
                    report.reporter.firstName || "Unknown"
                  )}
                </Typography>
              </Box>
            </Box>
          )}
          <Box display="flex" alignItems="center" style={{ gap: "8px" }}>
            <Chip
              label={
                report.status === "commentDeleted"
                  ? "COMMENT BANNED"
                  : report.status.toUpperCase()
              }
              variant="outlined"
              size="small"
              style={{
                color: "#fff",
                borderColor:
                  report.status === "open"
                    ? "#4caf50"
                    : report.status === "pending"
                      ? "#ff9800"
                      : report.status === "reviewing"
                        ? "#ff9800"
                        : report.status === "resolved"
                          ? "#2196f3"
                          : report.status === "commentDeleted"
                            ? "#f44336"
                            : "#999",
                backgroundColor:
                  report.status === "commentDeleted"
                    ? "rgba(244, 67, 54, 0.1)"
                    : "transparent",
              }}
            />
            {report?.status && (
              <Chip
                label={`Comment: ${report.status === "commentDeleted"
                  ? "BANNED"
                  : report.status.toUpperCase()
                  }`}
                variant="outlined"
                size="small"
                className={classes.postStatusBadge}
                style={{
                  color: "#fff",
                  borderColor:
                    report.status === "commentDeleted"
                      ? "#f44336"
                      : "#FE5A97",
                  backgroundColor:
                    report.status === "commentDeleted"
                      ? "rgba(244, 67, 54, 0.1)"
                      : "transparent",
                }}
              />
            )}
          </Box>
        </Box>

        <Divider style={{ backgroundColor: "#333", margin: "16px 0" }} />

        <Box>
          <Typography
            variant="body1"
            style={{ marginBottom: "8px", color: "#fff" }}
          >
            <strong>Comment ID:</strong>
            {report.commentId}
          </Typography>
          <Typography
            variant="body1"
            style={{ marginBottom: "8px", color: "#fff" }}
          >
            <strong>Post ID:</strong>
            {/* {report.commentId} */}
            <Link
              to={`/?postId=${report.commentDetails.galleryPostId}`}
              style={{
                color: "#FE5A97",
                textDecoration: "none",
              }}
              onMouseEnter={e => {
                e.currentTarget.style.textDecoration =
                  "underline";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.textDecoration = "none";
              }}
            >
              {report.commentDetails.galleryPostId}
            </Link>
          </Typography>
          <Typography
            variant="body1"
            style={{ marginBottom: "8px", color: "#fff" }}
          >
            <strong>Reason:</strong> {report.reason}
          </Typography>
          {report.commentText && (
            <Typography
              variant="body2"
              style={{ marginBottom: "8px", color: "#fff" }}
            >
              <strong>Comment:</strong> {report.commentText}
            </Typography>
          )}

          {(report.aiFlags && report.aiFlags.length > 0) && (
            <Typography
              variant="body2"
              style={{ marginBottom: "8px", color: "#fff" }}
            >
              <strong>AI Flags:</strong>
              <ul>
                {report.aiFlags.map(flag => (
                  <li key={flag._id}>
                    <strong>Field:</strong> {flag.field}<br />
                    <strong>Severity:</strong> {flag.severity}<br />
                    <strong>Provider:</strong> {flag.provider}<br />
                    <strong>Original Text:</strong> {flag.originalText}<br />
                    <strong>Matched Terms:</strong> {flag.matchedTerms.join(", ")}
                  </li>
                ))}
              </ul>
            </Typography>
          )}

          {/* Expandable Post Content */}
          <Box style={{ marginTop: "16px" }}>
            <Button
              className={classes.expandButton}
              onClick={() =>
                setExpandedPostReports(prev => ({
                  ...prev,
                  [report._id]: !prev[report._id],
                }))
              }
              size="small"
            >
              {expandedPostReports[report._id] ? "Hide" : "Show"} Comment Details
            </Button>
            {expandedPostReports[report._id] && (
              <Box className={classes.contentPreviewContainer}>
                <Typography
                  variant="body2"
                  className={classes.contentPreviewText}
                  style={{ color: "#fff", marginBottom: "12px" }}
                >
                  <strong>Reason:</strong> {report.reason}
                </Typography>
                {(report.commentText || report.comment?.comment) && (
                  <Typography
                    variant="body2"
                    className={classes.contentPreviewText}
                    style={{ color: "#fff", marginBottom: "16px" }}
                  >
                    <strong>Reported Comment:</strong>{" "}
                    {report.commentText || report.comment?.comment}
                  </Typography>
                )}
                {report.comment?.comment && (
                  <Typography
                    variant="body2"
                    className={classes.contentPreviewText}
                    style={{ color: "#fff", marginBottom: "16px" }}
                  >
                    <strong>Comment Description:</strong> {report.comment.comment}
                  </Typography>
                )}
                {report.comment?.content && report.comment.content.length > 0 && (
                  <Box>
                    <Typography
                      variant="body2"
                      style={{
                        color: "#FE5A97",
                        marginBottom: "8px",
                      }}
                    >
                      <strong>Comment Content:</strong>
                    </Typography>
                    <Box display="flex" flexWrap="wrap" style={{ gap: "8px" }}>
                      {report.comment.content.map((item, index) => {
                        const mediaKey = `comment_${report.commentId}_${index}`;
                        if (
                          item.mediaUrl &&
                          item.mediaUrl.startsWith("/api/files/")
                        ) {
                          if (!mediaUrls[mediaKey] && !loadingMedia[mediaKey]) {
                            fetchMediaUrl(item.mediaUrl, mediaKey);
                          }
                        }
                        const displayUrl = mediaUrls[mediaKey] || item.mediaUrl;

                        return (
                          <Box key={index} style={{ marginBottom: "8px" }}>
                            {item.type === "image" && (
                              <Box>
                                {loadingMedia[mediaKey] ? (
                                  <Box
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    style={{
                                      minHeight: "200px",
                                      width: "200px",
                                      backgroundColor: "#1a1a1a",
                                    }}
                                  >
                                    <CircularProgress size={24} />
                                  </Box>
                                ) : (
                                  <img
                                    src={displayUrl}
                                    alt={`Comment content ${index + 1}`}
                                    className={classes.contentPreviewImage}
                                    style={{
                                      maxWidth: "300px",
                                      maxHeight: "300px",
                                    }}
                                    onError={e => {
                                      (e.target as HTMLImageElement).src =
                                        defaultAvatars;
                                    }}
                                  />
                                )}
                                {item.paidContent && (
                                  <Chip
                                    label="Paid"
                                    size="small"
                                    style={{
                                      backgroundColor: "#FE5A97",
                                      color: "#fff",
                                      marginTop: "4px",
                                    }}
                                  />
                                )}
                              </Box>
                            )}
                            {item.type === "video" && (
                              <Box>
                                {loadingMedia[mediaKey] ? (
                                  <Box
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    style={{
                                      minHeight: "200px",
                                      width: "200px",
                                      backgroundColor: "#1a1a1a",
                                    }}
                                  >
                                    <CircularProgress size={24} />
                                  </Box>
                                ) : (
                                  <video
                                    src={displayUrl}
                                    controls
                                    className={classes.contentPreviewVideo}
                                    style={{
                                      maxWidth: "300px",
                                      maxHeight: "300px",
                                    }}
                                  >
                                    Your browser does not support the video tag.
                                  </video>
                                )}
                                {item.paidContent && (
                                  <Chip
                                    label="Paid"
                                    size="small"
                                    style={{
                                      backgroundColor: "#FE5A97",
                                      color: "#fff",
                                      marginTop: "4px",
                                    }}
                                  />
                                )}
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}
                {/* {report.comment?.price && (
                  <Typography
                    variant="body2"
                    style={{ color: "#FE5A97", marginTop: "16px" }}
                  >
                    <strong>Price:</strong> ${report.comment.price}
                  </Typography>
                )} */}
              </Box>
            )}
          </Box>
          <Typography variant="body2" style={{ color: "#ccc" }}>
            <strong>Reported:</strong> {formatDate(report.createdAt)}
          </Typography>
        </Box>

        <Box className={classes.actionButtons} style={{ marginTop: "16px" }}>
          {report.status !== "resolved" && report.status !== "commentDeleted" && (
            <Button
              variant="contained"
              className={classes.approveButton}
              onClick={() =>
                setReportStatusDialog({
                  open: true,
                  report,
                  status: "resolved",
                  reportType,
                })
              }
            >
              Warning
            </Button>
          )}
          {report.status !== "commentDeleted" && (
            <Button
              variant="contained"
              className={classes.rejectButton}
              onClick={() =>
                setReportStatusDialog({
                  open: true,
                  report,
                  status: "commentDeleted",
                  reportType: "comment",
                })
              }
            >
              Ban Comment
            </Button>
          )}
          {report.status === "commentDeleted" && (
            <Button
              variant="contained"
              className={classes.approveButton}
              onClick={() =>
                setReportStatusDialog({
                  open: true,
                  report,
                  status: "open",
                  reportType: "comment",
                })
              }
            >
              Recover Comment
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
